import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { GIFT_CARD_STEPS, DESIGN_COLORS, type GiftCardDesign, type GiftCardStep } from '@alterrego/module-carte-cadeau';
import { AdyenPaymentClient } from '@alterrego/module-payment';
import { apiFetch } from '../utils/api';
import './CarteCadeauScreen.css';

const CONFETTI_COLORS = ['#c8a96e', '#e8d5a8', '#6b2d3a', '#f7f4ee', '#1f4032'];

type OccasionKey =
  | 'birthday' | 'christmas' | 'mothers' | 'fathers'
  | 'wedding' | 'thanks' | 'valentines' | 'housewarming' | 'retirement';

const SPECIAL_OCCASIONS: Array<{ key: OccasionKey; label: string; emoji: string }> = [
  { key: 'birthday', label: 'Anniversaire', emoji: '🎂' },
  { key: 'christmas', label: 'Noël', emoji: '🎄' },
  { key: 'mothers', label: 'Fête des mères', emoji: '💐' },
  { key: 'fathers', label: 'Fête des pères', emoji: '👔' },
  { key: 'wedding', label: 'Mariage', emoji: '💍' },
  { key: 'thanks', label: 'Remerciement', emoji: '🙏' },
  { key: 'valentines', label: 'Saint-Valentin', emoji: '❤️' },
  { key: 'housewarming', label: 'Crémaillère', emoji: '🏠' },
  { key: 'retirement', label: 'Départ à la retraite', emoji: '🥂' },
];

const OCCASION_EMOJI: Record<OccasionKey, string> = {
  birthday: '🎂',
  christmas: '🎄',
  mothers: '💐',
  fathers: '👔',
  wedding: '💍',
  thanks: '🙏',
  valentines: '❤️',
  housewarming: '🏠',
  retirement: '🥂',
};
const DEFAULT_OCCASION_EMOJI = '✨';

// Suggested design per occasion (matching myPlace `occasionDesigns` intent,
// remapped to the alterrego palette). null = keep current design.
const OCCASION_DESIGNS: Partial<Record<OccasionKey, GiftCardDesign>> = {
  birthday: 'red',
  christmas: 'forest',
  valentines: 'red',
  wedding: 'gold',
  thanks: 'gold',
  mothers: 'wine',
  fathers: 'black',
  housewarming: 'bronze',
  retirement: 'gold',
};

const DEFAULT_INSPIRE_MESSAGES: string[] = [
  'Pour toi, avec toute mon affection. Profite de chaque instant !',
  'Parce que tu le mérites, tout simplement.',
  'Un petit bonheur à savourer, de ma part.',
  "Fais-toi plaisir, c'est le but !",
  'Pour toi, parce que les belles choses se partagent.',
];

const INSPIRE_MESSAGES: Record<OccasionKey, string[]> = {
  birthday: [
    'Joyeux anniversaire ! Que cette année soit aussi belle que toi.',
    'Un an de plus, un cadeau de plus. Profite bien !',
    "Pour tes [âge] ans, un moment d'exception rien que pour toi.",
    'Joyeux anniversaire ! Offre-toi ce qui te fait envie.',
  ],
  christmas: [
    'Joyeux Noël ! Un cadeau qui se déguste et se partage.',
    'Ho ho ho ! Le Père Noël a pensé à toi...',
    'Pour un Noël plein de saveurs et de surprises.',
  ],
  mothers: [
    'Merci pour tout, maman. Ce cadeau est pour toi.',
    'Pour la plus merveilleuse des mamans, avec amour.',
    "Maman, offre-toi un moment rien qu'à toi. Tu le mérites !",
  ],
  fathers: [
    'Papa, ce cadeau est pour toi. Fais-toi plaisir !',
    'Pour le meilleur des papas, avec toute mon admiration.',
    'Papa, à ton tour de te faire chouchouter !',
  ],
  wedding: [
    'Félicitations ! Trinquez à votre amour.',
    'Pour les plus beaux jours de votre vie, à deux.',
    'Que votre union soit aussi savoureuse que ce cadeau !',
  ],
  thanks: [
    'Merci du fond du cœur. Ce cadeau parle pour moi.',
    'Pour vous dire merci, avec toute ma gratitude.',
    'Un grand merci, vous avez fait la différence.',
  ],
  valentines: [
    'Mon cœur, ce cadeau est pour toi, avec tout mon amour.',
    'Pour nous deux, parce que chaque moment compte.',
    "Je t'aime. Savoure ce cadeau comme je savoure nos instants.",
  ],
  housewarming: [
    'Bienvenue chez vous ! De quoi inaugurer comme il se doit.',
    'Pour arroser votre nouveau chez-vous avec style !',
    'Félicitations pour votre nid douillet !',
  ],
  retirement: [
    'Félicitations ! Le meilleur reste à venir.',
    'À votre liberté ! Profitez de chaque instant.',
    "Vous l'avez bien mérité. Savourez !",
  ],
};

function pickInspireMessage(occasion: OccasionKey | null, current: string): string {
  const pool = (occasion && INSPIRE_MESSAGES[occasion]) || DEFAULT_INSPIRE_MESSAGES;
  const filtered = pool.filter((m) => m !== current);
  const candidates = filtered.length ? filtered : pool;
  return candidates[Math.floor(Math.random() * candidates.length)];
}

async function fetchInspiredMessage(payload: {
  occasion: OccasionKey | null;
  story?: string;
  recipient_name?: string;
  sender_name?: string;
  amount_eur?: number;
}): Promise<string> {
  const res = await apiFetch(`/gift-card/inspire-message`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // Backend zod enum tolère encore 'none' (cf routes/gift-card.ts OCCASIONS).
    // Quand le front n'a pas d'occasion sélectionnée → mappe sur 'none' pour
    // garder l'API inchangée et laisser Claude décider d'un ton neutre.
    body: JSON.stringify({ ...payload, occasion: payload.occasion ?? 'none' }),
  });
  if (!res.ok) {
    throw new Error(`inspire ${res.status}`);
  }
  const data = await res.json();
  if (typeof data.message !== 'string' || !data.message.trim()) {
    throw new Error('empty message');
  }
  return data.message;
}

function fireSuccessConfetti() {
  // Premium 3-burst cascade: center pop, then two side fans
  confetti({
    particleCount: 80,
    spread: 85,
    startVelocity: 45,
    origin: { x: 0.5, y: 0.35 },
    colors: CONFETTI_COLORS,
    zIndex: 1000,
  });
  setTimeout(() => {
    confetti({
      particleCount: 50,
      angle: 60,
      spread: 70,
      startVelocity: 50,
      origin: { x: 0, y: 0.6 },
      colors: CONFETTI_COLORS,
      zIndex: 1000,
    });
  }, 220);
  setTimeout(() => {
    confetti({
      particleCount: 50,
      angle: 120,
      spread: 70,
      startVelocity: 50,
      origin: { x: 1, y: 0.6 },
      colors: CONFETTI_COLORS,
      zIndex: 1000,
    });
  }, 380);
}

const BRAND_LOGO_URL ='https://ciss-media-dev.s3.fr-par.scw.cloud/pointofsales/47408/15-vauban-brc-187.png';

const AMOUNT_MIN = 50;
const AMOUNT_MAX = 250;
const AMOUNT_PRESETS = [50, 100, 150, 250] as const;
const MESSAGE_MAX = 280;

const AMOUNT_FEELINGS: Array<[number, string]> = [
  [50, 'Attentionné'],
  [80, 'Généreux'],
  [120, 'Grandiose'],
  [200, 'Inoubliable'],
  [250, 'Exceptionnel'],
];

function getFeeling(v: number): string {
  let f = AMOUNT_FEELINGS[0][1];
  for (const [threshold, label] of AMOUNT_FEELINGS) {
    if (v >= threshold) f = label;
  }
  return f;
}

function AdyenDropInStep({ amount, reference, metadata, onSuccess, onError }: {
  amount: number;
  reference: string;
  metadata: Record<string, string>;
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    (async () => {
      try {
        // 1. Create session via backend
        const sessionRes = await apiFetch(`/payments/sessions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount,
            currency: 'EUR',
            countryCode: 'FR',
            type: 'carte_cadeau',
            reference,
            returnUrl: window.location.href,
            metadata,
          }),
        });

        if (!sessionRes.ok) {
          const body = await sessionRes.text().catch(() => '');
          throw new Error(body || `Session failed (${sessionRes.status})`);
        }

        const session = await sessionRes.json();

        // 2. Mount Adyen Drop-in
        // Load Adyen CSS + JS depuis le bon environnement
        const cdnBase = session.environment === 'live'
          ? 'https://checkoutshopper-live.adyen.com'
          : 'https://checkoutshopper-test.adyen.com';
        const sdkVersion = '5.65.0';

        if (!document.getElementById('adyen-css')) {
          const link = document.createElement('link');
          link.id = 'adyen-css';
          link.rel = 'stylesheet';
          link.href = `${cdnBase}/checkoutshopper/sdk/${sdkVersion}/adyen.css`;
          document.head.appendChild(link);
        }

        if (!(window as any).AdyenCheckout) {
          await new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = `${cdnBase}/checkoutshopper/sdk/${sdkVersion}/adyen.js`;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load Adyen SDK'));
            document.head.appendChild(script);
          });
        }

        setLoading(false);

        const AdyenCheckout = (window as any).AdyenCheckout;
        if (!AdyenCheckout) {
          throw new Error('AdyenCheckout not loaded');
        }

        // Adyen v5 swallows session-setup failures in a silent `.catch`, leaving
        // `paymentMethodsResponse` undefined — `checkout.create('dropin')` then
        // crashes with "undefined is not an object (paymentMethodsResponse.paymentMethods)".
        // We capture the error via onError + flag and bail before mount.
        let setupError: any = null;
        const checkout = await AdyenCheckout({
          environment: session.environment,
          clientKey: session.clientKey,
          session: {
            id: session.id,
            sessionData: session.sessionData,
          },
          onPaymentCompleted: (result: any) => {
            if (result.resultCode === 'Authorised') onSuccess();
            else onError(result.resultCode ?? 'Paiement non autorise');
          },
          onError: (err: any) => {
            console.error('[Adyen] onError', err);
            if (!setupError) setupError = err;
            setError('Paiement: ' + (err?.message ?? JSON.stringify(err)));
          },
          locale: 'fr_FR',
          translations: {
            'fr_FR': {
              'paymentMethods.scheme': 'Carte bancaire',
              'paymentMethods.card': 'Carte bancaire',
              'creditCard.numberField.title': 'Numero de carte',
              'creditCard.expiryDateField.title': 'Date d\'expiration',
              'creditCard.cvcField.title': 'Code de securite',
              'creditCard.holderName': 'Nom du titulaire',
              'payButton': 'Payer',
              'storeDetails': 'Sauvegarder pour la prochaine fois',
            },
          },
          paymentMethodsConfiguration: {
            card: {
              hasHolderName: true,
              holderNameRequired: true,
              autoFocus: true,
              styles: {
                base: {
                  color: '#1a2332',
                  fontSize: '15px',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  fontWeight: '400',
                },
                placeholder: {
                  color: '#8a9ab0',
                  fontWeight: '400',
                },
                error: {
                  color: '#c0392b',
                },
              },
            },
          },
        });

        if (!containerRef.current) {
          throw new Error('Container ref is null');
        }

        // Detect silent setup failure. paymentMethodsResponse is set inside the SDK
        // only when /checkoutshopper/v1/sessions/{id}/setup succeeds. The most common
        // cause of failure is the clientKey not allowing the current Origin
        // (configured in Adyen Customer Area → API credentials → Allowed Origins).
        if (setupError || !(checkout as any).paymentMethodsResponse) {
          const detail = setupError?.message || setupError?.statusText || 'setup session échoué';
          throw new Error(
            `Le formulaire de paiement n'a pas pu être chargé (${detail}). ` +
            `Origine non autorisée par Adyen ? (${window.location.origin})`
          );
        }

        checkout.create('dropin', {
          paymentMethodsConfiguration: {
            card: {
              name: 'Carte bancaire',
            },
          },
        }).mount(containerRef.current);
      } catch (err: any) {
        setError(err?.message ?? 'Erreur inconnue');
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="gc-step">
      <h2>Paiement sécurisé</h2>
      <div className="gc-dropin-amount">{(amount / 100).toFixed(0)} €</div>
      {loading && <p className="gc-dropin-loading">Chargement du formulaire de paiement...</p>}
      {error && <p className="gc-dropin-error">{error}</p>}
      <div ref={containerRef} className="gc-dropin-container" />
    </div>
  );
}

const COLORS: { key: GiftCardDesign; label: string; color: string }[] = [
  { key: 'platinum', label: 'Platine', color: '#4a4e5a' },
  { key: 'black', label: 'Noir', color: '#1a1a25' },
  { key: 'bronze', label: 'Bronze', color: '#8b5a2b' },
  { key: 'silver', label: 'Argent', color: '#7d8287' },
  { key: 'gold', label: 'Or', color: '#c8a96e' },
  { key: 'wine', label: 'Lie de vin', color: '#6b2d3a' },
  { key: 'forest', label: 'Vert bouteille', color: '#1f4032' },
  { key: 'red', label: 'Rouge passion', color: '#c8102e' },
];

interface State {
  step: number;
  amount: number;
  design: GiftCardDesign;
  recipientName: string;
  message: string;
  senderName: string;
  occasion: OccasionKey | null;
  story: string;
}

// CardPreview lives at module scope so React keeps a stable DOM tree between
// renders. The previous version was defined inside CarteCadeauScreen, which
// caused the flipper element to be remounted on every parent re-render
// (lost pointer capture mid-drag).
type CardPreviewProps = {
  amount: number;
  bgColor: string;
  recipientName: string;
  message: string;
  senderName: string;
  occasionEmoji: string;
  float?: boolean;
  interactive?: boolean;
};
const TAP_THRESHOLD_PX = 6;

function CardPreview({ amount, bgColor, recipientName, message, senderName, occasionEmoji, float = false, interactive = true }: CardPreviewProps) {
  const [isFlipped, setIsFlipped] = useState(false);
  const [dragRotation, setDragRotation] = useState<number | null>(null);
  const dragRef = useRef<{
    x: number; y: number; baseRotation: number;
    pointerId: number; target: HTMLElement;
    committed: boolean;
  } | null>(null);

  const toggleFlip = () => { if (interactive) setIsFlipped((v) => !v); };
  const onKey = (e: React.KeyboardEvent) => {
    if (!interactive) return;
    if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleFlip(); }
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!interactive) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    dragRef.current = {
      x: e.clientX, y: e.clientY,
      baseRotation: isFlipped ? 180 : 0,
      pointerId: e.pointerId,
      target: e.currentTarget,
      committed: false,
    };
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = dragRef.current;
    if (!start || e.pointerId !== start.pointerId) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (!start.committed) {
      if (Math.abs(dx) < TAP_THRESHOLD_PX && Math.abs(dy) < TAP_THRESHOLD_PX) return;
      if (Math.abs(dy) > Math.abs(dx)) {
        // Vertical-dominant motion: let the page scroll instead.
        dragRef.current = null;
        return;
      }
      start.committed = true;
      try { start.target.setPointerCapture(e.pointerId); } catch { /* noop */ }
    }
    const cardWidth = start.target.clientWidth || 320;
    setDragRotation(start.baseRotation + (dx / cardWidth) * 180);
  };
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = dragRef.current;
    if (!start || e.pointerId !== start.pointerId) return;
    dragRef.current = null;
    try { start.target.releasePointerCapture(e.pointerId); } catch { /* noop */ }
    if (!start.committed) {
      toggleFlip();
      return;
    }
    setDragRotation((r) => {
      if (r === null) return null;
      const normalized = ((r % 360) + 360) % 360;
      const showBack = normalized > 90 && normalized < 270;
      setIsFlipped(showBack);
      return null;
    });
  };
  const onPointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = dragRef.current;
    dragRef.current = null;
    if (start) { try { start.target.releasePointerCapture(e.pointerId); } catch { /* noop */ } }
    setDragRotation(null);
  };

  const flipped = interactive && isFlipped;
  const dragging = dragRotation !== null;
  const flipperStyle = dragging
    ? { transform: `rotateY(${dragRotation}deg)`, transition: 'none' as const }
    : undefined;

  return (
    <div className={`gc-card-wrap ${float ? 'gc-card-wrap-float' : ''}`}>
      <div className={`gc-card-floater ${float ? 'gc-card-float' : ''}`}>
        <div
          className={`gc-card-flipper ${flipped && !dragging ? 'is-flipped' : ''} ${interactive ? 'is-interactive' : ''} ${dragging ? 'is-dragging' : ''}`}
          style={flipperStyle}
          onPointerDown={interactive ? onPointerDown : undefined}
          onPointerMove={interactive ? onPointerMove : undefined}
          onPointerUp={interactive ? onPointerUp : undefined}
          onPointerCancel={interactive ? onPointerCancel : undefined}
          onKeyDown={onKey}
          role={interactive ? 'button' : undefined}
          tabIndex={interactive ? 0 : -1}
          aria-pressed={interactive ? flipped : undefined}
          aria-label={interactive ? (flipped ? 'Voir le recto' : 'Voir le verso') : undefined}
        >
          {/* Recto = côté "boutique" générique : logo + brand + montant +
              pill Carte Cadeau. Aucun champ personnalisé ici — c'est la
              face vendue en magasin (mimique d'une carte cadeau physique). */}
          <div className="gc-card gc-card-face gc-card-face-front" style={{ background: bgColor }}>
            <div className="gc-card-header">
              <div className="gc-card-identity">
                <img className="gc-card-logo" src={BRAND_LOGO_URL} alt="" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                <span className="gc-brand">Sauf Imprevu</span>
              </div>
            </div>
            <div className="gc-card-amount-block">
              <span className="gc-amount">{amount}<span className="gc-amount-unit">€</span></span>
            </div>
            <div className="gc-card-footer">
              <span className="gc-badge">Carte Cadeau</span>
            </div>
          </div>
          {/* Verso = côté "manuscrit" personnel : occasion + Pour + message +
              signature. Pas de re-duplication brand/amount (déjà vus au recto
              avant le flip). Empty state élégant si pas encore de saisie. */}
          <div className="gc-card gc-card-face gc-card-face-back" style={{ background: bgColor }}>
            <span className="gc-card-back-pattern" aria-hidden="true" />
            {recipientName || message || senderName ? (
              <>
                <div className="gc-card-back-occasion" aria-hidden="true">{occasionEmoji}</div>
                <div className="gc-card-back-body">
                  {recipientName && <div className="gc-card-back-to">Pour {recipientName}</div>}
                  {message && <div className="gc-card-back-msg">{message}</div>}
                </div>
                <div className="gc-card-back-footer">
                  {senderName && <span className="gc-card-back-from">— {senderName}</span>}
                </div>
              </>
            ) : (
              <div className="gc-card-back-empty">
                <div className="gc-card-back-occasion" aria-hidden="true">✨</div>
                <p>Le verso sera personnalisé après votre saisie.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * `preview` — aperçu design réservé au staff (`/staff/carte-cadeau`). Le parcours
 * et le rendu sont identiques à la version client, à deux exceptions près :
 *  - le step 4 (Drop-in Adyen) est sauté : aucune session de paiement n'est
 *    créée, la vente reste fermée (VITE_ENABLE_GIFT_CARDS inchangé) ;
 *  - le step 5 n'appelle pas `/payments/:ref/gift-card-link` (pas de paiement,
 *    donc pas de lien) et n'affiche pas le bouton de partage.
 */
export function CarteCadeauScreen({ preview = false }: { preview?: boolean } = {}) {
  const navigate = useNavigate();
  const closeTo = preview ? '/staff/dashboard' : '/';
  const [s, setS] = useState<State>({
    step: 0, amount: 100, design: 'platinum',
    recipientName: '', message: '', senderName: '',
    occasion: null, story: '',
  });

  // Lien partageable iOS Share Sheet — récupéré du backend après paiement
  // Adyen success. Pas de persistence côté Hono : c'est le CRM Boostore qui
  // créera/stockera la carte cadeau ; le backend Hono retourne juste le lien
  // (stub pour l'instant, cf payments.ts).
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [creatingGiftCard, setCreatingGiftCard] = useState(false);
  const [giftCardError, setGiftCardError] = useState<string | null>(null);

  // Reference Adyen stable pour ce flow carte cadeau — utilisée comme clé
  // d'idempotency vers Adyen ET comme clé future Boostore CRM. Lazy init
  // pour rester constante entre re-renders.
  const [paymentReference] = useState(() =>
    `GC_${Date.now()}_${Math.random().toString(36).slice(2, 8).toUpperCase()}`
  );

  const [specialModalOpen, setSpecialModalOpen] = useState(false);
  const [storyModalOpen, setStoryModalOpen] = useState(false);
  const [storyDraft, setStoryDraft] = useState('');
  const storyTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Explicit focus on the StoryModal textarea avec un delay qui laisse
  // l'animation de présentation finir (matches FreeAmountSheet pattern).
  // `autoFocus` seul ne suffit pas sur iOS WKWebView : le clavier monte trop
  // tôt et le scroll-into-view rate. Le delay de 250ms aligne avec la
  // gcFadeUp 250ms du modal-overlay.
  useEffect(() => {
    if (!storyModalOpen) return;
    const t = setTimeout(() => storyTextareaRef.current?.focus(), 250);
    return () => clearTimeout(t);
  }, [storyModalOpen]);

  const set = (patch: Partial<State>) => setS((prev) => ({ ...prev, ...patch }));
  // En aperçu staff, le step 3 mène directement à l'écran final : le step 4
  // monterait le Drop-in Adyen et créerait une session de paiement.
  const next = () => set({ step: preview && s.step === 3 ? 5 : Math.min(s.step + 1, 5) });
  const prev = () => set({ step: Math.max(s.step - 1, 0) });
  const bgColor = COLORS.find((c) => c.key === s.design)?.color ?? '#4a4e5a';

  // Swipe horizontal pour naviguer entre les steps form (0..3). Désactivé sur
  // step 4 (Adyen Drop-in — il gère son propre flow) et step 5 (succès, dot
  // de fin). Ignore le drag qui démarre sur :
  //  - .gc-card-flipper → la card preview a son propre drag-to-flip (commit 648499d)
  //  - inputs/textarea/buttons / sliders → éviter le hijack des controls
  //  - .gc-modal-overlay → si une modal est ouverte, swipe inactif
  // Seuils : 70px déplacement horizontal min, |dy| doit rester ≤ |dx|/1.5
  // (mouvement clairement horizontal, sinon on laisse le scroll vertical).
  const SWIPE_THRESHOLD_PX = 70;
  const swipeRef = useRef<{ x: number; y: number; id: number; ignore: boolean } | null>(null);
  const onContentPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const target = e.target as HTMLElement;
    const ignore =
      !!target.closest('.gc-card-flipper') ||
      !!target.closest('input') ||
      !!target.closest('textarea') ||
      !!target.closest('button') ||
      !!target.closest('.gc-modal-overlay') ||
      s.step > 3; // pas de swipe-step pendant Adyen (4) / succès (5) ; review (3) garde swipe-right back
    swipeRef.current = { x: e.clientX, y: e.clientY, id: e.pointerId, ignore };
  };
  const onContentPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = swipeRef.current;
    swipeRef.current = null;
    if (!start || e.pointerId !== start.id || start.ignore) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
    if (Math.abs(dy) > Math.abs(dx) / 1.5) return; // trop vertical
    if (dx < 0) {
      // Swipe left → next, mais on ne déclenche PAS Adyen depuis swipe
      // (s.step < 3 = peut aller jusqu'à 3, pas au-delà).
      if (s.step < 3) next();
    } else {
      if (s.step > 0) prev();
    }
  };

  const isSpecialOccasion = s.occasion !== null;
  const specialOccasionLabel = useMemo(
    () => (s.occasion ? SPECIAL_OCCASIONS.find((o) => o.key === s.occasion) : undefined),
    [s.occasion]
  );
  const inspireDisabled = false;

  const [inspireLoading, setInspireLoading] = useState(false);
  const [inspireToast, setInspireToast] = useState<string | null>(null);

  const handleInspire = async () => {
    // Static path: no story -> draw from the static dictionary.
    if (!s.story.trim()) {
      const next = pickInspireMessage(s.occasion, s.message);
      set({ message: next.slice(0, MESSAGE_MAX) });
      return;
    }
    // Story present -> call backend for a personalized message.
    setInspireLoading(true);
    setInspireToast(null);
    try {
      const message = await fetchInspiredMessage({
        occasion: s.occasion,
        story: s.story,
        recipient_name: s.recipientName || undefined,
        sender_name: s.senderName || undefined,
        amount_eur: s.amount,
      });
      set({ message: message.slice(0, MESSAGE_MAX) });
    } catch {
      // Graceful fallback on network / API errors.
      const next = pickInspireMessage(s.occasion, s.message);
      set({ message: next.slice(0, MESSAGE_MAX) });
      setInspireToast('Pas de connexion, voici une suggestion.');
      setTimeout(() => setInspireToast(null), 3500);
    } finally {
      setInspireLoading(false);
    }
  };

  const openStoryModal = () => {
    setStoryDraft(s.story);
    setStoryModalOpen(true);
  };
  const saveStory = () => {
    set({ story: storyDraft });
    setStoryModalOpen(false);
  };
  const cancelStory = () => {
    setStoryModalOpen(false);
  };
  // Pick an occasion in the modal, or pass null to clear ("Aucune occasion").
  const pickSpecialOccasion = (key: OccasionKey | null) => {
    if (key === null) {
      set({ occasion: null });
    } else {
      const suggested = OCCASION_DESIGNS[key];
      set(suggested ? { occasion: key, design: suggested } : { occasion: key });
    }
    setSpecialModalOpen(false);
  };

  const feeling = useMemo(() => getFeeling(s.amount), [s.amount]);
  const sliderPct = useMemo(
    () => ((s.amount - AMOUNT_MIN) / (AMOUNT_MAX - AMOUNT_MIN)) * 100,
    [s.amount]
  );
  const sliderBg = `linear-gradient(90deg, #c8a96e 0%, #c8a96e ${sliderPct}%, var(--color-bg-subtle, #f0f3f7) ${sliderPct}%, var(--color-bg-subtle, #f0f3f7) 100%)`;

  // Premium confetti burst when reaching success step
  useEffect(() => {
    if (s.step === 5) fireSuccessConfetti();
  }, [s.step]);

  const occasionEmoji = (s.occasion && OCCASION_EMOJI[s.occasion]) || DEFAULT_OCCASION_EMOJI;

  return (
    <div className="carte-cadeau">
      <div className="gc-header">
        {s.step > 0 && s.step < 5 && (
          <button
            type="button"
            className="gc-back-header"
            onClick={prev}
            aria-label="Étape précédente"
          >
            ‹
          </button>
        )}
        <button
          type="button"
          className="gc-close"
          onClick={() => navigate(closeTo)}
          aria-label="Fermer la carte cadeau"
        >
          ×
        </button>
        <span className="gc-header-brand">SAUF IMPREVU</span>
        <span className="gc-header-title">Carte Cadeau</span>
      </div>

      {/* Step dots — 4 form steps (0..3) ; Adyen (4) et succès (5) hors dots */}
      <div className="gc-dots">
        {[0,1,2,3].map((i) => (
          <span key={i} className={`gc-dot ${i <= s.step ? 'active' : ''} ${i === s.step ? 'current' : ''}`} />
        ))}
      </div>

      <div
        className="gc-content"
        onPointerDown={onContentPointerDown}
        onPointerUp={onContentPointerUp}
      >
        {/* Step 0: Amount */}
        {s.step === 0 && (
          <div className="gc-step">
            <h2 className="fade-up fade-up-1">Combien souhaitez-vous offrir ?</h2>
            <div className="fade-up fade-up-2"><CardPreview key={s.step} float interactive={false} amount={s.amount} bgColor={bgColor} recipientName={s.recipientName} message={s.message} senderName={s.senderName} occasionEmoji={occasionEmoji} /></div>

            <div className="gc-amount-presets fade-up fade-up-3">
              {AMOUNT_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={`gc-amount-btn ${s.amount === preset ? 'selected' : ''}`}
                  onClick={() => set({ amount: preset })}
                >
                  {preset}€
                </button>
              ))}
            </div>

            <div className="gc-amount-slider-wrap fade-up fade-up-4">
              <div className="gc-amount-display">{s.amount} €</div>
              <div className="gc-amount-feeling">{feeling}</div>
              <input
                type="range"
                min={AMOUNT_MIN}
                max={AMOUNT_MAX}
                step={5}
                value={s.amount}
                onChange={(e) => set({ amount: Number(e.target.value) })}
                className="gc-slider"
                style={{ background: sliderBg }}
              />
              <div className="gc-range-labels"><span>{AMOUNT_MIN} €</span><span>{AMOUNT_MAX} €</span></div>
            </div>
          </div>
        )}

        {/* Step 1: Design */}
        {s.step === 1 && (
          <div className="gc-step">
            <h2 className="fade-up fade-up-1">Affinons le style</h2>
            <div className="fade-up fade-up-2"><CardPreview key={s.step} float interactive={false} amount={s.amount} bgColor={bgColor} recipientName={s.recipientName} message={s.message} senderName={s.senderName} occasionEmoji={occasionEmoji} /></div>
            <div className="gc-colors fade-up fade-up-3">
              {COLORS.map((c) => (
                <button key={c.key} className={`gc-swatch ${s.design === c.key ? 'active' : ''}`}
                  style={{ background: c.color }} onClick={() => set({ design: c.key })} />
              ))}
            </div>
            <p className="gc-color-label fade-up fade-up-4">{COLORS.find((c) => c.key === s.design)?.label}</p>
          </div>
        )}

        {/* Step 2: Recipient */}
        {s.step === 2 && (
          <div className="gc-step">
            <h2 className="fade-up fade-up-1">Signons la carte</h2>

            <div className="gc-occasion-block fade-up fade-up-2">
              <label className="gc-occasion-block-label">Occasion (optionnel)</label>
              <button
                type="button"
                className={`gc-occasion-picker ${isSpecialOccasion ? 'selected' : ''}`}
                onClick={() => setSpecialModalOpen(true)}
              >
                <span className="gc-occasion-emoji">
                  {specialOccasionLabel?.emoji ?? DEFAULT_OCCASION_EMOJI}
                </span>
                <span className="gc-occasion-picker-label">
                  {specialOccasionLabel ? specialOccasionLabel.label : 'Choisir une occasion'}
                </span>
                <span className="gc-occasion-arrow">▾</span>
              </button>
            </div>

            <div className="fade-up fade-up-3">
              <label>Pour</label>
              <input value={s.recipientName} onChange={(e) => set({ recipientName: e.target.value })}
                placeholder="Marie, Les Dupont, Maman..." />
            </div>
            <div className="fade-up fade-up-3">
              <label>De la part de</label>
              <input value={s.senderName} onChange={(e) => set({ senderName: e.target.value })}
                placeholder="Votre nom, un surnom..." />
            </div>
            <div className="fade-up fade-up-4">
              <div className="gc-field-header">
                <label>Votre message</label>
                <button
                  type="button"
                  className={`gc-inspire-btn ${inspireLoading ? 'loading' : ''} ${s.story ? 'has-story' : ''}`}
                  onClick={handleInspire}
                  disabled={inspireDisabled || inspireLoading}
                  aria-label={s.story ? 'Suggérer un message personnalisé' : 'Suggérer un message'}
                  aria-busy={inspireLoading}
                >
                  {inspireLoading ? (
                    <><span className="gc-inspire-spinner" aria-hidden="true" /> J'imagine…</>
                  ) : (
                    <>
                      ✨ Inspirez-moi
                      {s.story && <span className="gc-inspire-dot" aria-hidden="true" />}
                    </>
                  )}
                </button>
              </div>
              <textarea
                value={s.message}
                onChange={(e) => set({ message: e.target.value.slice(0, MESSAGE_MAX) })}
                placeholder="Pour toi, avec toute mon affection..."
                rows={4}
                maxLength={MESSAGE_MAX}
              />
              <div className="gc-char-count">{s.message.length}/{MESSAGE_MAX}</div>
              {/* Story progressive enhancement: tapping the link records context
                  that `handleInspire` then passes to Claude for a sur-mesure msg. */}
              <button
                type="button"
                className={`gc-story-link ${s.story ? 'has-story' : ''}`}
                onClick={openStoryModal}
              >
                {s.story
                  ? '✓ Histoire enregistrée — Modifier'
                  : '+ Raconter le contexte pour un message sur-mesure'}
              </button>
              {inspireToast && <div className="gc-inspire-toast" role="status">{inspireToast}</div>}
            </div>
          </div>
        )}

        {/* Step 3: Review — la card preview EST le récap visuel. Drag-to-flip
            activé ICI uniquement (conflit avec swipe inter-steps ailleurs).
            Sous la card, une micro-instruction pour signaler le flip + bloc
            transparent "comment ça marche" qui rassure sur le mode de remise. */}
        {s.step === 3 && (
          <div className="gc-step gc-review-step">
            <div className="fade-up fade-up-1">
              <CardPreview key={s.step} float interactive amount={s.amount} bgColor={bgColor} recipientName={s.recipientName} message={s.message} senderName={s.senderName} occasionEmoji={occasionEmoji} />
            </div>
            <p className="gc-review-hint fade-up fade-up-2">
              Touchez la carte pour voir le message personnalisé.
            </p>
            <div className="gc-experience-card fade-up fade-up-3">
              <p className="gc-experience-header">
                <span className="gc-experience-emoji" aria-hidden="true">💌</span>
                <span>Une remise personnalisée en plusieurs temps</span>
              </p>
              <p className="gc-experience-line">
                <strong>{s.recipientName || 'Votre destinataire'}</strong> recevra
                d'abord un teaser mystérieux…
              </p>
              <p className="gc-experience-line">
                Puis découvrira votre carte cadeau personnalisée avec votre message
                rien que pour {s.recipientName ? 'elle ou lui' : 'votre destinataire'}.
              </p>
            </div>
            <div className="gc-delivery-preview fade-up fade-up-4">
              <p className="gc-delivery-preview-header">
                Après paiement, vous pourrez partager le lien avec{' '}
                <strong>{s.recipientName || 'votre destinataire'}</strong>
              </p>
              <div className="gc-delivery-preview-chips" aria-hidden="true">
                <span className="gc-delivery-chip">
                  <span className="gc-delivery-chip-icon">💬</span>
                  <span className="gc-delivery-chip-label">Messages</span>
                </span>
                <span className="gc-delivery-chip">
                  <span className="gc-delivery-chip-icon">📧</span>
                  <span className="gc-delivery-chip-label">Mail</span>
                </span>
                <span className="gc-delivery-chip">
                  <span className="gc-delivery-chip-icon">📱</span>
                  <span className="gc-delivery-chip-label">WhatsApp</span>
                </span>
                <span className="gc-delivery-chip">
                  <span className="gc-delivery-chip-icon">📤</span>
                  <span className="gc-delivery-chip-label">Autres</span>
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Adyen Drop-in — jamais monté en aperçu staff */}
        {s.step === 4 && !preview && (
          <AdyenDropInStep
            amount={s.amount * 100}
            reference={paymentReference}
            metadata={{
              recipientName: s.recipientName,
              senderName: s.senderName,
              occasion: s.occasion ?? '',
            }}
            onSuccess={async () => {
              // Transition immédiate vers le step 5 → l'écran de succès affiche
              // "Génération du lien…" pendant le fetch placeholder vers Hono.
              set({ step: 5 });
              setCreatingGiftCard(true);
              setGiftCardError(null);
              try {
                const res = await apiFetch(
                  `/payments/${encodeURIComponent(paymentReference)}/gift-card-link`,
                  { method: 'GET' },
                );
                if (!res.ok) {
                  const body = await res.text().catch(() => '');
                  throw new Error(body || `Lien indisponible (${res.status})`);
                }
                const data = await res.json() as { shareUrl: string };
                setShareUrl(data.shareUrl);
              } catch (err: any) {
                console.warn('[gift-card-link] fetch failed', err);
                setGiftCardError(err?.message ?? 'Création du lien impossible');
              } finally {
                setCreatingGiftCard(false);
              }
            }}
            onError={(msg) => alert('Paiement échoué : ' + msg)}
          />
        )}

        {/* Step 5: Success + iOS Share Sheet */}
        {s.step === 5 && (
          <div className="gc-step gc-success-step gc-ceremony-step">
            <h2 className="gc-ceremony-title">
              Votre carte cadeau pour <strong>{s.recipientName || 'votre destinataire'}</strong> est prête à être remise
            </h2>
            <CardPreview key={s.step} float interactive amount={s.amount} bgColor={bgColor} recipientName={s.recipientName} message={s.message} senderName={s.senderName} occasionEmoji={occasionEmoji} />
            <p className="gc-ceremony-confirm-line">
              ✓ Paiement confirmé · {s.amount} €
            </p>
            <ul className="gc-journey-list" aria-label={
              s.recipientName
                ? `Ce qui attend ${s.recipientName}`
                : 'Ce qui attend le destinataire'
            }>
              <li>
                <span className="gc-journey-emoji" aria-hidden="true">🎁</span>
                Une notification mystérieuse pour ouvrir l'attente
              </li>
              <li>
                <span className="gc-journey-emoji" aria-hidden="true">✨</span>
                La découverte de sa carte cadeau personnalisée
              </li>
              <li>
                <span className="gc-journey-emoji" aria-hidden="true">💝</span>
                Votre message rien que pour{' '}
                {s.recipientName ? `${s.recipientName}` : 'lui'}, au verso
              </li>
            </ul>
            {creatingGiftCard && (
              <p className="gc-success-delivery">Génération du lien…</p>
            )}
            {giftCardError && !creatingGiftCard && (
              <p className="gc-success-delivery gc-success-error">{giftCardError}</p>
            )}
            {shareUrl && !creatingGiftCard && (
              <>
                <button
                  type="button"
                  className="gc-share-cta gc-ceremony-cta"
                  onClick={async () => {
                    const text = s.message
                      ? `${s.message}\n\n${shareUrl}`
                      : `Une carte cadeau t'attend chez Sauf Imprévu : ${shareUrl}`;
                    // navigator.share → UIActivityViewController natif sur iOS
                    // WKWebView (Messages / Mail / WhatsApp / AirDrop / Copier).
                    if (typeof navigator !== 'undefined' && (navigator as any).share) {
                      try {
                        await (navigator as any).share({
                          title: '🎁 Une carte cadeau pour vous',
                          text,
                          url: shareUrl,
                        });
                      } catch { /* user cancelled — no-op */ }
                    } else {
                      try {
                        await navigator.clipboard.writeText(shareUrl);
                        alert('Lien copié dans le presse-papier');
                      } catch {
                        alert(shareUrl);
                      }
                    }
                  }}
                >
                  {s.recipientName ? `Remettre la carte à ${s.recipientName}` : 'Remettre la carte'}
                </button>
                <p className="gc-ceremony-tagline">
                  Le destinataire recevra un lien sécurisé.
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Special occasions modal */}
      {specialModalOpen && (
        <div
          className="gc-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Pour quelle occasion ?"
          onClick={(e) => { if (e.target === e.currentTarget) setSpecialModalOpen(false); }}
        >
          <div className="gc-modal gc-occasion-modal">
            <div className="gc-modal-title">Pour quelle occasion ?</div>
            <ul className="gc-occasion-list">
              <li>
                <button
                  type="button"
                  className={`gc-occasion-item gc-occasion-item-none ${s.occasion === null ? 'selected' : ''}`}
                  onClick={() => pickSpecialOccasion(null)}
                >
                  <span className="gc-occasion-emoji">{DEFAULT_OCCASION_EMOJI}</span>
                  Aucune occasion
                </button>
              </li>
              {SPECIAL_OCCASIONS.map((o) => (
                <li key={o.key}>
                  <button
                    type="button"
                    className={`gc-occasion-item ${s.occasion === o.key ? 'selected' : ''}`}
                    onClick={() => pickSpecialOccasion(o.key)}
                  >
                    <span className="gc-occasion-emoji">{o.emoji}</span>
                    {o.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Story modal */}
      {storyModalOpen && (
        <div
          className="gc-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Racontez votre histoire"
          onClick={(e) => { if (e.target === e.currentTarget) cancelStory(); }}
        >
          <div className="gc-modal gc-story-modal">
            <button type="button" className="gc-modal-close" onClick={cancelStory} aria-label="Fermer">×</button>
            <div className="gc-story-emoji">💝</div>
            <div className="gc-story-title">Racontez-nous...</div>
            <div className="gc-story-intro">
              Prenez un instant. Pourquoi ce cadeau ?<br />
              Qu'est-ce qui vous touche chez cette personne ?<br />
              Les émotions rendent les cadeaux uniques...
            </div>
            <textarea
              ref={storyTextareaRef}
              className="gc-story-textarea"
              value={storyDraft}
              onChange={(e) => setStoryDraft(e.target.value)}
              placeholder="Racontez votre histoire ici..."
              rows={6}
            />
            <div className="gc-story-actions">
              <button type="button" className="gc-modal-cancel" onClick={cancelStory}>Annuler</button>
              <button type="button" className="gc-modal-save" onClick={saveStory}>C'est dit, avec le cœur</button>
            </div>
          </div>
        </div>
      )}

      {/* Footer CTA */}
      <div className="gc-footer">
        {s.step > 0 && s.step < 4 && <button className="gc-back" onClick={prev}>Retour</button>}
        {s.step < 3 && <button className="gc-cta" onClick={next}>Continuer</button>}
        {s.step === 3 && <button className="gc-cta" onClick={next}>Offrir pour {s.amount} €</button>}
        {/* Step 4 : pas de CTA — le Drop-in Adyen gère le flow */}
        {s.step === 5 && <button className="gc-cta" onClick={() => navigate(closeTo)}>Fermer</button>}
      </div>
    </div>
  );
}
